import { api } from './axios';

/**
 * Text API — клиент для управления текстовыми работами
 * 
 * Text — это отдельная сущность для текстовых работ, которая может быть
 * использована в Work вместе с Track или отдельно.
 * 
 * @see work_api.md
 */

// ==================== Создание и получение текстов ====================

/**
 * Создать текстовую работу
 * @param {Object} data - Данные текста
 * @param {string} [data.battlePersonId] - ID участника баттла
 * @param {string} [data.duelParticipantId] - ID участника дуэли
 * @param {string} data.title - Название текста (обязательно)
 * @param {string} [data.description] - Описание
 * @param {string} data.content - Основной текст (обязательно)
 * @param {string} [data.status] - Статус: SUBMITTED, APPROVED, DISQUALIFIED, DRAFT
 * @returns {Promise<Object>} Созданный текст
 */
export const createText = (data) => {
    return api.post('/texts', data).then((res) => res.data);
};

/**
 * Получить текст по ID
 * @param {string} id - ID текста
 * @returns {Promise<Object>} Детали текста
 */
export const getTextById = (id) => {
    return api.get(`/texts/${id}`).then((res) => res.data);
};

/**
 * Обновить текст
 * @param {string} id - ID текста
 * @param {Object} data - Данные для обновления
 * @param {string} [data.title] - Название
 * @param {string} [data.description] - Описание
 * @param {string} [data.content] - Основной текст
 * @param {string} [data.status] - Статус
 * @returns {Promise<Object>} Обновлённый текст
 */
export const updateText = (id, data) => {
    return api.put(`/texts/${id}`, data).then((res) => res.data);
};

/**
 * Удалить текст
 * @param {string} id - ID текста
 * @returns {Promise<void>}
 */
export const deleteText = (id) => {
    return api.delete(`/texts/${id}`).then((res) => res.data);
};

// ==================== Тексты участника ====================

/**
 * Получить тексты участника баттла
 * @param {string} battlePersonId - ID участника баттла
 * @returns {Promise<Array>} Список текстов участника
 */
export const getTextsByBattlePerson = (battlePersonId) => {
    return api.get(`/texts/person/battle/${battlePersonId}`).then((res) => res.data);
};

/**
 * Получить тексты участника дуэли
 * @param {string} duelParticipantId - ID участника дуэли
 * @returns {Promise<Array>} Список текстов участника
 */
export const getTextsByDuelParticipant = (duelParticipantId) => {
    return api.get(`/texts/person/duel/${duelParticipantId}`).then((res) => res.data);
};

// ==================== Модерация текстов ====================

/**
 * Одобрить текст (ADMIN/MODERATOR)
 * @param {string} id - ID текста
 * @returns {Promise<Object>} Обновлённый текст
 */
export const approveText = (id) => {
    return api.post(`/texts/${id}/approve`).then((res) => res.data);
};

/**
 * Дисквалифицировать текст (ADMIN/MODERATOR)
 * @param {string} id - ID текста
 * @param {string} reason - Причина дисквалификации (макс. 500 символов)
 * @returns {Promise<Object>} Обновлённый текст
 */
export const disqualifyText = (id, reason) => {
    return api.post(`/texts/${id}/disqualify`, { reason }).then((res) => res.data);
};

export default {
    createText,
    getTextById,
    updateText,
    deleteText,
    getTextsByBattlePerson,
    getTextsByDuelParticipant,
    approveText,
    disqualifyText
};
